import { MainTemplate } from "@/components/templates";
import { PageTitle } from "@/components/molecules";
import { ErrorWords } from "@/components/organisms/ErrorWords";
import { ErrorCode } from "@/libs/errors";
import { css } from "@emotion/react";
import {
  BizsolReturnButton,
  ConnectBoardReturnButton,
} from "@/components/atoms";
import { useState } from "react";
import { Services } from "@/models";
import { useServicesForPublicQuery } from "@/generated/graphql";
import config from "@/libs/config";
import { SignOutButton } from "@/components/atoms/SignOutButton";
import useRollbarUtil from "../hooks/useRollbarUtil";
import type { getNotifyErrorHandlerForGraphQL } from "../hooks/useRollbarUtil";

const supportPageURL = "https://support.id.x.moneyforward.com/hc/ja";

type Props = {
  code: ErrorCode;
};

export const ErrorPage = ({ code }: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const displayBizsolReturnButton = config.IS_SHIKOKUBANK;
  const displayConnectBoardReturnButton = config.IS_DHBK;

  var errorText: React.ReactNode = (
    <ErrorWords>
      {errorMessage(code, getNotifyErrorHandlerForGraphQL)}
    </ErrorWords>
  );
  if (
    displayBizsolReturnButton &&
    code != ErrorCode.BIZSOL_COMPANY_ALREADY_REGISTERED &&
    code != ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY
  ) {
    errorText = appendBizsolReturnButton(errorText);
  }
  if (
    displayConnectBoardReturnButton &&
    code != ErrorCode.CONNBOARD_COMPANY_ALREADY_REGISTERED &&
    code != ErrorCode.CONNBOARD_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY
  ) {
    errorText = appendConnectBoardReturnButton(errorText);
  }
  if (code == ErrorCode.NO_REGISTRATION_COMPANY_INFO) {
    errorText = appendSignOutButton(errorText);
  }

  return (
    <MainTemplate title={`${config.BANK_NICKNAME} Mikatano ID`}>
      <PageTitle mainTitle={errorPageTitle(code)} />
      {errorText}
    </MainTemplate>
  );
};

const errorPageTitle = (code: ErrorCode): string => {
  switch (code) {
    case ErrorCode.NO_REGISTRATION_COMPANY_INFO:
      return "事業者の登録が完了していないためご利用できません";
    default:
      return `${config.BANK_NICKNAME} Mikatano ID`;
  }
};

const errorMessage = (
  code: ErrorCode,
  getNotifyErrorHandler: getNotifyErrorHandlerForGraphQL
): React.ReactNode => {
  switch (code) {
    case ErrorCode.INTERNAL_SERVER_ERROR:
      return InternalServerError(getNotifyErrorHandler);
    case ErrorCode.ACCESS_DENIED:
      return <>アクセスが拒否されました</>;
    case ErrorCode.LOGIN_REQUIRED_TO_VERIFY_EMAIL:
      return (
        <>
          メールの認証には、事前のログインが必要です。
          <br />
          <br />
          まずはログイン画面にて、
          <br />
          お使いのID・パスワードを使ってログインしてください。
          <br />
          その後、再度メール記載の認証用URLにアクセスしてください。
        </>
      );
    case ErrorCode.FAILED_TO_VERIFY_EMAIL_ADDRESS:
      return <>メールアドレスの認証に失敗しました</>;
    case ErrorCode.INVALID_PASSWORD_RESET_TOKEN:
    case ErrorCode.INVALID_EMAIL_AUTH_TOKEN:
      return (
        <>
          メールの有効期限が切れている、
          <br />
          または何かしらの問題が発生している可能性があります。
          <br />
          再度、メール送信をおこなってください。
          <br />
          <br />
          または、事業者内のMikatano シリーズ管理者に
          <br />
          お問い合わせください。
        </>
      );
    case ErrorCode.INVALID_REQUEST_PARAMETERS:
      return defaultMessage;
    case ErrorCode.INVALID_REQUEST_HEADERS:
      return defaultMessage;
    case ErrorCode.NO_REGISTRATION_COMPANY_INFO:
      return (
        <>
          ご利用には管理者による事業者の登録が必要です
          <br />
          詳細は所属する事業者内のMikatano ID管理者にお問合せください。
        </>
      );
    case ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR:
      return defaultMessage;
    case ErrorCode.BIZSOL_COMPANY_ALREADY_REGISTERED:
      return (
        <>
          エラーが発生しました。
          <br />
          要因として、下記が考えられます。
          <br />
          <br />
          <div css={divWapper}>
            ・既に事業者内でMikatano IDに登録されている
            <br />
            <br />
            ＜解決策＞
            <br />
            {config.BIZSOL_CLIENT_ID === "0175" ? (
              <>Mikatano 資金管理</>
            ) : (
              <>Mikatano シリーズ</>
            )}
            の利用を開始するには、管理者から招待を受けてください。
            <br />
            なお、既に招待を受けている場合には、
            <a href={config.XID_URL + "/sign-in-with-id"} css={aWapper}>
              初回ログイン
            </a>
            を行ってください。
            <br />
            詳細は
            <a
              href={supportPageURL + "/p/bizid3"}
              css={aWapper}
              target="_blank"
            >
              こちら
            </a>
            をご確認ください。
          </div>
        </>
      );
    case ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY:
      return (
        <>
          エラーが発生しました。
          <br />
          要因として、下記が考えられます。
          <br />
          <br />
          <div css={divWapper}>
            ・{config.BIZSOL_DISPLAY_NAME}
            の権限が管理者もしくは一般
            <br />
            <br />
            ＜解決策＞
            <br />
            Mikatano IDの初回登録が出来るのは
            {config.BIZSOL_DISPLAY_NAME}
            の「マスター」権限の方のみです。
            <br />
            「マスター」権限の方にMikatano IDの初回登録を依頼してください。
            <br />
            初回登録後、招待を受けることでご利用を開始いただけます。
            <br />
            詳細は{" "}
            <a
              href={supportPageURL + "/p/bizid3"}
              css={aWapper}
              target="_blank"
            >
              こちら
            </a>
            をご確認ください。
          </div>
        </>
      );
    case ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_SIGN_IN:
      return defaultMessage;
    case ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_CONNECT:
      return defaultMessage;
    case ErrorCode.BIZSOL_TOKEN_IS_EXPIRED:
      return (
        <>
          一定時間が経過したため、
          {config.BIZSOL_DISPLAY_NAME}
          の認証が切れました。 <br />
          再度{config.BIZSOL_DISPLAY_NAME}
          の認証操作を行っていただく必要があります。
        </>
      );
    case ErrorCode.BIZSOL_INVALID_ID_TOKEN:
      return defaultMessage;
    case ErrorCode.BIZSOL_INVALID_REGISTRATION_TOKEN:
      return defaultMessage;
    case ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_BE_REGISTERED:
      return (
        <>
          Mikatanoの新規登録の受付は終了いたしました。
          <br />
          すでにアカウントをお持ちのお客さまは、
          <br />
          登録済みのログイン情報にて引き続きご利用いただけます。
        </>
      );
    case ErrorCode.BIZSOL_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT:
      return (
        <>
          ログイン状態が変更されたためログアウトしました。
          <br />
          お手数ですが、再度ログイン操作をお願いします。
        </>
      );
    case ErrorCode.ONLY_ADMINISTRATOR_CAN_OPERATE_IT:
      return "権限がありません。事業者内のMikatano シリーズ管理者にお問い合わせください。";
    default:
      return defaultMessage;
  }
};

const defaultMessage = (
  <>
    メンテナンス中、または何らかの障害が発生している可能性があります。
    <br />
    メンテナンス終了もしくは復旧までしばらくお待ちください。
  </>
);

const InternalServerError = (
  getNotifyErrorHandler: getNotifyErrorHandlerForGraphQL
): React.ReactNode => {
  const [services, setServices] = useState(new Services());
  useServicesForPublicQuery({
    onCompleted: (data) => {
      if (data.servicesForPublic) {
        setServices(new Services(data.servicesForPublic));
      }
    },
    onError: getNotifyErrorHandler("Failed to get services information"),
  });

  return (
    <>
      <div css={msgWapper}>
        {defaultMessage}
        <br />
        <br />
        なお詳細については、各サービスの「よくある質問」より「サービスからのお知らせ」をご確認ください。
        <br />
        <br />
        <ul css={olWapper}>
          {services.bfm.isEnabled() && (
            <li>
              ・
              <a
                css={aWapper}
                href="https://support.bfm.x.moneyforward.com/hc/ja"
                target="_blank"
              >
                Mikatano 資金管理
              </a>
            </li>
          )}

          {services.invoice.isEnabled() && (
            <li>
              ・
              <a
                css={aWapper}
                href="https://support.bill.x.moneyforward.com/hc/ja"
                target="_blank"
              >
                Mikatano インボイス管理
              </a>
            </li>
          )}

          {services.dxf.isEnabled() && (
            <li>
              ・
              <a
                css={aWapper}
                href="https://support.dxf.x.moneyforward.com/hc/ja"
                target="_blank"
              >
                Mikarano ワークス
              </a>
            </li>
          )}
        </ul>
      </div>
    </>
  );
};

const appendBizsolReturnButton = (child: React.ReactNode): React.ReactNode => {
  return (
    <>
      <div>
        <BizsolReturnButton />
      </div>
      <div>{child}</div>
    </>
  );
};

const appendSignOutButton = (child: React.ReactNode): React.ReactNode => {
  return (
    <>
      <div>{child}</div>
      <div>
        <SignOutButton />
      </div>
    </>
  );
};

const appendConnectBoardReturnButton = (
  child: React.ReactNode
): React.ReactNode => {
  return (
    <>
      <div>
        <ConnectBoardReturnButton />
      </div>
      <div>{child}</div>
    </>
  );
};

const msgWapper = css({
  width: "100%",
  textAlign: "left",
});

const olWapper = css({
  width: "100%",
  padding: "0",
});

const aWapper = css({
  color: "#1A73E8",
});

const divWapper = css({
  width: "450px",
  textAlign: "left",
  margin: "0 auto",
  paddingLeft: "30px",
});
